import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { format } from 'date-fns';
import { ArrowLeft, ArrowRight, Banknote, CalendarIcon, CheckCircle2, CreditCard, Lock, X } from 'lucide-react';
import { DUMPSTERS, getDumpster, formatCurrency } from '../../data/dumpsters';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Calendar } from '../ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup';
import { Progress } from '../ui/Progress';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { initialReservation, STEP_LABELS, type PaymentMethod, type ReservationState } from './types';
import { OrderSummary } from './OrderSummary';
interface ReservationFlowProps {
  open: boolean;
  initialSizeId: string | null;
  onClose: () => void;
  onComplete: (state: ReservationState) => void;
}
export function ReservationFlow({
  open,
  initialSizeId,
  onClose,
  onComplete
}: ReservationFlowProps) {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<ReservationState>(initialReservation);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open) {
      setStep(0);
      setDone(false);
      setSubmitting(false);
      setErrors({});
      setState({
        ...initialReservation,
        sizeId: initialSizeId
      });
    }
  }, [open, initialSizeId]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  const progress = useMemo(() => (done ? STEP_LABELS.length : step) / STEP_LABELS.length * 100, [step, done]);
  function validateStep(): boolean {
    const e: Record<string, string> = {};
    if (step === 0 && !state.sizeId) e.size = 'Please choose a dumpster size.';
    if (step === 1) {
      if (!state.deliveryDate) e.date = 'Select a delivery date.';
      if (!state.address.street.trim()) e.street = 'Street address is required.';
      if (!state.address.city.trim()) e.city = 'City is required.';
      if (!state.address.state.trim()) e.state = 'State is required.';
      if (!/^\d{5}$/.test(state.address.zip.trim())) e.zip = 'Enter a valid 5-digit ZIP.';
    }
    if (step === 2) {
      if (!state.contact.firstName.trim()) e.firstName = 'First name is required.';
      if (!state.contact.lastName.trim()) e.lastName = 'Last name is required.';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(state.contact.email.trim())) e.email = 'Enter a valid email.';
      if (state.contact.phone.replace(/\D/g, '').length < 10) e.phone = 'Enter a valid phone number.';
    }
    if (step === 3) {
      if (!state.card.name.trim()) e.cardName = 'Name on card is required.';
      if (state.card.number.replace(/\s/g, '').length < 15) e.cardNumber = 'Enter a valid card number.';
      if (!/^\d{2}\s*\/\s*\d{2}$/.test(state.card.expiry.trim())) e.expiry = 'Use MM/YY format.';
      if (state.card.cvc.replace(/\D/g, '').length < 3) e.cvc = 'Enter a valid CVC.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  function next() {
    if (!validateStep()) return;
    if (step < STEP_LABELS.length - 1) {
      setStep(s => s + 1);
    } else {
      submit();
    }
  }
  function submit() {
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      onComplete(state);
    }, 1200);
  }
  function back() {
    setErrors({});
    setStep(s => Math.max(0, s - 1));
  }
  if (!open) return null;
  const dumpster = getDumpster(state.sizeId);
  return <AnimatePresence data-id="element-30">
      <motion.div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 backdrop-blur-sm sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="dialog" aria-modal="true" aria-label="Reserve a dumpster" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-31">
        <motion.div className="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-background shadow-xl" initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} data-id="element-32">
          <div className="flex items-center justify-between border-b border-border px-6 py-4" data-id="element-33">
            <div data-id="element-34">
              <h2 className="text-lg font-semibold text-foreground" data-id="element-35">
                {done ? 'Reservation confirmed' : 'Reserve a dumpster'}
              </h2>
              {!done && <p className="text-sm text-muted-foreground" data-id="element-36">
                  Step {step + 1} of {STEP_LABELS.length} — {STEP_LABELS[step]}
                </p>}
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground" data-id="element-37">
              <X className="h-5 w-5" data-id="element-38" />
            </button>
          </div>

          {!done && <Progress value={progress} className="h-1 rounded-none" data-id="element-39" />}

          <div className="max-h-[70vh] overflow-y-auto px-6 py-6" data-id="element-40">
            {done ? <ConfirmationView state={state} data-id="element-41" /> : <div className="grid gap-8 md:grid-cols-[1fr_18rem]" data-id="element-42">
                <div className="min-w-0" data-id="element-43">
                  <AnimatePresence mode="wait" data-id="element-44">
                    <motion.div key={step} initial={{
                  opacity: 0,
                  x: 12
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -12
                }} transition={{
                  duration: 0.18
                }} data-id="element-45">
                      {step === 0 && <StepSize value={state.sizeId} error={errors.size} onChange={sizeId => setState(s => ({
                    ...s,
                    sizeId
                  }))} data-id="element-46" />}
                      {step === 1 && <StepDelivery state={state} setState={setState} errors={errors} data-id="element-47" />}
                      {step === 2 && <StepContact state={state} setState={setState} errors={errors} data-id="element-48" />}
                      {step === 3 && <StepPayment state={state} setState={setState} errors={errors} data-id="element-49" />}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="md:pt-1" data-id="element-50">
                  <OrderSummary state={state} data-id="element-51" />
                </div>
              </div>}
          </div>

          {!done && <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4" data-id="element-52">
              {step > 0 ? <Button variant="ghost" onClick={back} className="gap-2" data-id="element-53">
                  <ArrowLeft className="h-4 w-4" data-id="element-54" /> Back
                </Button> : <span data-id="element-55" />}
              <Button onClick={next} disabled={submitting} className="gap-2" data-id="element-56">
                {submitting ? 'Processing…' : step === STEP_LABELS.length - 1 ? <>
                    {state.paymentMethod === 'card-now' ? `Pay ${dumpster ? formatCurrency(dumpster.price) : ''} & reserve` : 'Confirm reservation'}
                  </> : <>
                    Continue <ArrowRight className="h-4 w-4" data-id="element-57" />
                  </>}
              </Button>
            </div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
/* ---------- Step 1: Size ---------- */
function StepSize({
  value,
  error,
  onChange
}: {
  value: string | null;
  error?: string;
  onChange: (id: string) => void;
}) {
  return <div data-id="element-58">
      <h3 className="text-base font-semibold text-foreground" data-id="element-59">
        Choose your dumpster size
      </h3>
      <p className="mt-1 text-sm text-muted-foreground" data-id="element-60">
        Every size includes a 7-day rental, delivery, pickup, and disposal.
      </p>
      {error && <p className="mt-2 text-sm text-destructive" data-id="element-61">{error}</p>}

      <div className="mt-4 space-y-3" data-id="element-62">
        {DUMPSTERS.map(d => {
        const selected = value === d.id;
        return <button key={d.id} type="button" onClick={() => onChange(d.id)} aria-pressed={selected} className={cn('flex w-full items-center justify-between rounded-xl border p-4 text-left transition-colors', selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-foreground/30')} data-id="element-63">
              <div className="min-w-0" data-id="element-64">
                <div className="flex items-center gap-2" data-id="element-65">
                  <span className="font-medium text-foreground" data-id="element-66">{d.name}</span>
                  {d.popular && <Badge variant="secondary" data-id="element-67">Popular</Badge>}
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-68">
                  {d.bestFor}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-69">
                  {d.dimensions} · up to {d.weightLimit}
                </p>
              </div>
              <span className="ml-4 shrink-0 text-right" data-id="element-70">
                <span className="block text-lg font-semibold text-foreground" data-id="element-71">
                  {formatCurrency(d.price)}
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-72">flat</span>
              </span>
            </button>;
      })}
      </div>
    </div>;
}
/* ---------- Step 2: Delivery ---------- */
function StepDelivery({
  state,
  setState,
  errors
}: {
  state: ReservationState;
  setState: React.Dispatch<React.SetStateAction<ReservationState>>;
  errors: Record<string, string>;
}) {
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);
  return <div className="space-y-5" data-id="element-73">
      <div data-id="element-74">
        <h3 className="text-base font-semibold text-foreground" data-id="element-75">
          Delivery details
        </h3>
        <p className="mt-1 text-sm text-muted-foreground" data-id="element-76">
          Where and when should we drop off your dumpster?
        </p>
      </div>

      <div data-id="element-77">
        <Label data-id="element-78">Delivery date</Label>
        <Popover data-id="element-79">
          <PopoverTrigger asChild data-id="element-80">
            <Button variant="outline" className={cn('mt-1.5 w-full justify-start gap-2 font-normal', !state.deliveryDate && 'text-muted-foreground')} aria-invalid={!!errors.date} data-id="element-81">
              <CalendarIcon className="h-4 w-4" data-id="element-82" />
              {state.deliveryDate ? format(state.deliveryDate, 'EEEE, MMMM d, yyyy') : 'Pick a date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-id="element-83">
            <Calendar selected={state.deliveryDate} onSelect={date => setState(s => ({
            ...s,
            deliveryDate: date
          }))} data-id="element-84" />
          </PopoverContent>
        </Popover>
        {errors.date && <p className="mt-1 text-sm text-destructive" data-id="element-85">{errors.date}</p>}
      </div>

      <Field label="Street address" error={errors.street} data-id="element-86">
        <Input value={state.address.street} placeholder="123 Main St" aria-invalid={!!errors.street} onChange={e => setState(s => ({
        ...s,
        address: {
          ...s.address,
          street: e.target.value
        }
      }))} data-id="element-87" />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-88">
        <Field label="City" error={errors.city} data-id="element-89">
          <Input value={state.address.city} aria-invalid={!!errors.city} onChange={e => setState(s => ({
          ...s,
          address: {
            ...s.address,
            city: e.target.value
          }
        }))} data-id="element-90" />
        </Field>
        <Field label="State" error={errors.state} data-id="element-91">
          <Input value={state.address.state} maxLength={2} placeholder="TX" aria-invalid={!!errors.state} onChange={e => setState(s => ({
          ...s,
          address: {
            ...s.address,
            state: e.target.value.toUpperCase()
          }
        }))} data-id="element-92" />
        </Field>
        <Field label="ZIP code" error={errors.zip} data-id="element-93">
          <Input value={state.address.zip} inputMode="numeric" maxLength={5} aria-invalid={!!errors.zip} onChange={e => setState(s => ({
          ...s,
          address: {
            ...s.address,
            zip: e.target.value.replace(/\D/g, '')
          }
        }))} data-id="element-94" />
        </Field>
      </div>

      <Field label="Placement notes (optional)" data-id="element-95">
        <Textarea value={state.address.notes} placeholder="e.g. Place on the left side of the driveway." rows={3} onChange={e => setState(s => ({
        ...s,
        address: {
          ...s.address,
          notes: e.target.value
        }
      }))} data-id="element-96" />
      </Field>
    </div>;
}
/* ---------- Step 3: Contact ---------- */
function StepContact({
  state,
  setState,
  errors
}: {
  state: ReservationState;
  setState: React.Dispatch<React.SetStateAction<ReservationState>>;
  errors: Record<string, string>;
}) {
  const set = (key: keyof ReservationState['contact']) => (v: string) => setState(s => ({
    ...s,
    contact: {
      ...s.contact,
      [key]: v
    }
  }));
  return <div className="space-y-5" data-id="element-97">
      <div data-id="element-98">
        <h3 className="text-base font-semibold text-foreground" data-id="element-99">
          Your contact information
        </h3>
        <p className="mt-1 text-sm text-muted-foreground" data-id="element-100">
          We'll use this to confirm your reservation and coordinate delivery.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-101">
        <Field label="First name" error={errors.firstName} data-id="element-102">
          <Input value={state.contact.firstName} aria-invalid={!!errors.firstName} onChange={e => set('firstName')(e.target.value)} data-id="element-103" />
        </Field>
        <Field label="Last name" error={errors.lastName} data-id="element-104">
          <Input value={state.contact.lastName} aria-invalid={!!errors.lastName} onChange={e => set('lastName')(e.target.value)} data-id="element-105" />
        </Field>
      </div>

      <Field label="Email" error={errors.email} data-id="element-106">
        <Input type="email" value={state.contact.email} placeholder="you@example.com" aria-invalid={!!errors.email} onChange={e => set('email')(e.target.value)} data-id="element-107" />
      </Field>

      <Field label="Phone" error={errors.phone} data-id="element-108">
        <Input type="tel" value={state.contact.phone} placeholder="(555) 123-4567" aria-invalid={!!errors.phone} onChange={e => set('phone')(e.target.value)} data-id="element-109" />
      </Field>
    </div>;
}
/* ---------- Step 4: Payment ---------- */
function StepPayment({
  state,
  setState,
  errors
}: {
  state: ReservationState;
  setState: React.Dispatch<React.SetStateAction<ReservationState>>;
  errors: Record<string, string>;
}) {
  const setCard = (key: keyof ReservationState['card']) => (v: string) => setState(s => ({
    ...s,
    card: {
      ...s.card,
      [key]: v
    }
  }));
  const options: {
    id: PaymentMethod;
    title: string;
    body: string;
    icon: React.ElementType;
  }[] = [{
    id: 'card-now',
    title: 'Pay now by card',
    body: 'Charge your card today to fully secure your reservation.',
    icon: CreditCard
  }, {
    id: 'card-on-file',
    title: 'Card on file — pay cash on delivery',
    body: "We'll hold your card to secure the booking. Pay with cash when we deliver.",
    icon: Banknote
  }];
  return <div className="space-y-5" data-id="element-110">
      <div data-id="element-111">
        <h3 className="text-base font-semibold text-foreground" data-id="element-112">Payment</h3>
        <p className="mt-1 text-sm text-muted-foreground" data-id="element-113">
          Choose how you'd like to pay. A card is required to secure your
          reservation.
        </p>
      </div>

      <RadioGroup value={state.paymentMethod} onValueChange={v => setState(s => ({
      ...s,
      paymentMethod: v as PaymentMethod
    }))} className="space-y-3" data-id="element-114">
        {options.map(opt => {
        const selected = state.paymentMethod === opt.id;
        return <label key={opt.id} htmlFor={opt.id} className={cn('flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors', selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border')} data-id="element-115">
              <RadioGroupItem id={opt.id} value={opt.id} className="mt-0.5" data-id="element-116" />
              <span className="flex-1" data-id="element-117">
                <span className="flex items-center gap-2 font-medium text-foreground" data-id="element-118">
                  <opt.icon className="h-4 w-4 text-primary" data-id="element-119" />
                  {opt.title}
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground" data-id="element-120">
                  {opt.body}
                </span>
              </span>
            </label>;
      })}
      </RadioGroup>

      <div className="rounded-xl border border-border p-4" data-id="element-121">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-122">
          <Lock className="h-4 w-4 text-primary" data-id="element-123" />
          Card details
        </div>
        <div className="mt-4 space-y-4" data-id="element-124">
          <Field label="Name on card" error={errors.cardName} data-id="element-125">
            <Input value={state.card.name} aria-invalid={!!errors.cardName} onChange={e => setCard('name')(e.target.value)} data-id="element-126" />
          </Field>
          <Field label="Card number" error={errors.cardNumber} data-id="element-127">
            <Input value={state.card.number} inputMode="numeric" placeholder="1234 5678 9012 3456" aria-invalid={!!errors.cardNumber} onChange={e => setCard('number')(e.target.value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim())} data-id="element-128" />
          </Field>
          <div className="grid grid-cols-2 gap-4" data-id="element-129">
            <Field label="Expiry (MM/YY)" error={errors.expiry} data-id="element-130">
              <Input value={state.card.expiry} inputMode="numeric" placeholder="MM/YY" aria-invalid={!!errors.expiry} onChange={e => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
              const formatted = digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
              setCard('expiry')(formatted);
            }} data-id="element-131" />
            </Field>
            <Field label="CVC" error={errors.cvc} data-id="element-132">
              <Input value={state.card.cvc} inputMode="numeric" placeholder="123" aria-invalid={!!errors.cvc} onChange={e => setCard('cvc')(e.target.value.replace(/\D/g, '').slice(0, 4))} data-id="element-133" />
            </Field>
          </div>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-134">
          <Lock className="h-3 w-3" data-id="element-135" />
          This is a demo checkout — no real card is charged.
        </p>
      </div>
    </div>;
}
/* ---------- Confirmation ---------- */
function ConfirmationView({
  state
}: {
  state: ReservationState;
}) {
  const dumpster = getDumpster(state.sizeId);
  const confirmationNo = useMemo(() => `GW-${Math.floor(100000 + Math.random() * 899999)}`, []);
  return <div className="text-center" data-id="element-136">
      <motion.span initial={{
      scale: 0.6,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 18
    }} className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary" data-id="element-137">
        <CheckCircle2 className="h-9 w-9" data-id="element-138" />
      </motion.span>
      <h3 className="mt-5 text-xl font-semibold text-foreground" data-id="element-139">
        You're all set!
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground" data-id="element-140">
        Your {dumpster?.name} dumpster is reserved
        {state.deliveryDate ? ` for ${format(state.deliveryDate, 'MMMM d, yyyy')}` : ''}
        . A confirmation has been sent to {state.contact.email || 'your email'}.
      </p>

      <div className="mx-auto mt-6 max-w-sm rounded-xl border border-border bg-secondary/40 p-5 text-left text-sm" data-id="element-141">
        <Row label="Confirmation #" value={confirmationNo} data-id="element-142" />
        <Row label="Size" value={dumpster ? `${dumpster.name} (${dumpster.yards} yd)` : '—'} data-id="element-143" />
        <Row label="Delivery" value={state.deliveryDate ? format(state.deliveryDate, 'MMM d, yyyy') : '—'} data-id="element-144" />
        <Row label="Payment" value={state.paymentMethod === 'card-now' ? `Paid by card${dumpster ? ` · ${formatCurrency(dumpster.price)}` : ''}` : 'Card on file · cash on delivery'} data-id="element-145" />
      </div>

      <p className="mt-5 text-xs text-muted-foreground" data-id="element-146">
        We'll call {state.contact.phone || 'you'} to confirm your delivery
        window.
      </p>
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between py-1.5" data-id="element-147">
      <span className="text-muted-foreground" data-id="element-148">{label}</span>
      <span className="font-medium text-foreground" data-id="element-149">{value}</span>
    </div>;
}
function Field({
  label,
  error,
  children
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-150">
      <Label className="mb-1.5 block" data-id="element-151">{label}</Label>
      {children}
      {error && <p className="mt-1 text-sm text-destructive" data-id="element-152">{error}</p>}
    </div>;
}